/// <amd-module name="Widgets/SprintBurndownConfig/SprintBurndownConfig" />
import { isNameValid } from "Dashboards/CommonConfig/NameField";
import { SizePicker } from "Dashboards/CommonConfig/SizePicker";
import { ConfigProps, LoadingStatus } from "Dashboards/WidgetContracts/Internal/ConfigProps";
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import * as React from "react";
import * as Core from "Tfs/Platform/Contracts/Core";
import { VssComponent } from "VSS/Platform/Layout";
import { IListBoxItem } from "VSSUI/ListBox";
import { SprintBurndownSettings } from "Widgets/SprintBurndownWidget/SprintBurndownSettings";
import { filterSettings, SprintBurndownSettingsManager } from "Widgets/SprintBurndownWidget/SprintBurndownSettingsManager";
import { renderTeamPicker, renderVersionToggle } from "./CommonConfigRenderer";
import { IToggleConfigMode } from "./IToggleConfigMode";
import { getTeamListItems } from "./Pickers/OpStoreTeamsPickerHelper";

interface BurndownConfigState {
    size: WidgetGridSize;
    widgetSettings: SprintBurndownSettings;

    initialLoadComplete: boolean;

    teamOptions: IListBoxItem<Core.WebApiTeam>[];
}

export class SprintBurndownConfig extends VssComponent<ConfigProps & IToggleConfigMode, BurndownConfigState> {
    public static readonly componentType = "SprintBurndownConfig";

    constructor(props: ConfigProps & IToggleConfigMode) {
        super(props);
        this.state = {
            size: this.props.config.size,
            widgetSettings: {} as any,

            initialLoadComplete: false,
            teamOptions: []
        };
    }

    public async componentDidMount(): Promise<void> {
        try {
            const settingsManager = new SprintBurndownSettingsManager(this.context.pageContext, this.props.teamContext);
            let widgetSettings: SprintBurndownSettings =
                this.props.config.settings != null ? JSON.parse(this.props.config.settings) : await settingsManager.generateDefaultSettings();

            // Settings may have been downgraded from Modern config. Ensure settings is just the members of the legacy config contract
            widgetSettings = filterSettings(widgetSettings);

            // Step 1: Get Team Options
            const teams = await this.trackPromise(getTeamListItems(this.context.pageContext, widgetSettings.team.projectId)).promise;

            this.setState(
                {
                    initialLoadComplete: true,
                    widgetSettings: widgetSettings,
                    teamOptions: teams
                },
                () => {
                    if (this.props.isReload) {
                        this.notifyParentOfUpdate();
                    } else {
                        this.props.parentProps.onConfigLoaded({ outcome: LoadingStatus.success }); // The configuration experience becomes visible after this point.
                    }
                }
            );
        } catch (e) {
            // Something went wrong. Now an error is presented.
            this.props.parentProps.onConfigLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: e
            });
        }
    }

    public componentDidUpdate(prevProps: Readonly<ConfigProps>): void {
        if (prevProps.saveValidationTimeStamp !== this.props.saveValidationTimeStamp) {
            const currentConfig = this.props.config;
            this.props.parentProps.onValidationResponse(currentConfig, this.isConfigValid(currentConfig));
        }
    }

    public render(): JSX.Element | null {
        if (!this.state.initialLoadComplete) {
            return null;
        }
        const isInModernMode = false;

        return (
            <div className="sprint-burndown-config">
                {
                    <SizePicker
                        key="size-picker"
                        onChange={this.onSizeChanged}
                        sizes={this.props.contributionConstraints.supportedSizes}
                        value={this.props.config.size}
                    />
                }
                {renderTeamPicker(this.onTeamSelect, this.state.teamOptions, this.state.widgetSettings.team.teamId)}
                {renderVersionToggle(this.onToggleLegacyMode, isInModernMode, this.context.pageContext)}
            </div>
        );
    }

    private isConfigValid(props: DashboardWidgetProps): boolean {
        return isNameValid(props.name) && SprintBurndownSettingsManager.settingsAreValid(this.state.widgetSettings);
    }

    /** EVENT HANDLERS */
    /* Pass Size changes up to parent. */
    private onSizeChanged = (newSize: WidgetGridSize): void => {
        this.setState({ size: newSize }, this.notifyParentOfUpdate);
    };

    private onTeamSelect = async (teamItem: IListBoxItem<Core.WebApiTeam>): Promise<void> => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.team.teamId = teamItem.id;
        const localState = {
            widgetSettings: newSettings
        } as BurndownConfigState;
        this.setState(localState, this.notifyParentOfUpdate);
    };

    private notifyParentOfUpdate = (): void => {
        this.props.parentProps.onUpdate({ ...this.props.config, size: this.state.size, settings: JSON.stringify(this.state.widgetSettings) });
    };

    private onToggleLegacyMode = (): void => {
        // Request a switch to the modern config
        this.props.onToggleConfig(false);
    };
}

VssComponent.register(SprintBurndownConfig.componentType, SprintBurndownConfig);
